import { getImageServer } from "../utils/get-image-server";
import { akamai } from "../globals";
import { debug } from "../utils/debug-log";
import medias from "./media-configs";

const { Media } = akamai.amp;

// Define supported media types for the Akamai AMP player
const MediaTypes = {
  m3u8: "application/x-mpegURL",
  mp4: "video/mp4",
};

// Class to configure AMP video player settings
export class AmpVideoConfig extends akamai.amp.Config {
  /**
   * Constructs a new AMP video config instance.
   * @param {Object} settings - Player configuration settings.
   */
  constructor(settings) {
    super({ autoplay: settings.autoplay });
    debug.log("[AmpConfig] Constructor settings:", settings);
    this._settings = settings;
    this._muted = settings.muted ?? true;

    // Set autoplay policy based on autoplay and mute settings
    this._autoplayPolicy = this.calculateAutoplayPolicy();
  }

  /**
   * Calculate the autoplay policy based on mute and autoplay settings.
   * @returns {string} - Autoplay policy setting.
   */
  calculateAutoplayPolicy() {
    if (this.muted === false && this.autoplay === true) {
      return "none";
    } else if (this.autoplay === true && this.muted === true) {
      return "mutedinline";
    }
    return "default";
  }

  /** Getter and Setter for autoplay property. */
  get autoplay() {
    return super.autoplay || this.settings.autoplay;
  }
  set autoplay(flag = true) {
    debug.log("[AmpConfig] SET autoplay:", flag);
    super.autoplay = flag;
  }

  /** Getter and Setter for autoplayPolicy property. */
  get autoplayPolicy() {
    return "muted";
  }
  set autoplayPolicy(value = "default") {
    debug.log("[AmpConfig] SET autoplayPolicy:", value);
    this._autoplayPolicy = value;
  }

  /** Getter for domain property. */
  get domain() {
    return "webmd.com";
  }

  /** Getter for branding configuration. */
  get branding() {
    return {};
  }
  get captioning() {
    //const settingsCaptioning = this._settings?.captioning ?? {};
    //const captioning = { ...super.captioning, ...settingsCaptioning };
    const captioning = super.captioning;
    debug.log("[AmpConfig][captioning][GET] captioning:", captioning);
    return captioning;
  }
  set captioning(data) {
    debug.log("[AmpConfig][captioning][SET] data:", data);
    const settingsCaptioning = this._settings?.captioning ?? {};
    const captioning = { enabled: true, renderer: akamai.amp.CaptionRenderer.AUTO, ...super.captioning, ...settingsCaptioning, ...data };
    debug.log("[AmpConfig][captioning][SET] captioning:", captioning);
    super.captioning = captioning;
  }
  /** Getter for context menu settings. */
  get contextmenu() {
    return null;
  }

  /**
   * Getter and Setter for controls configuration.
   * Includes default and conditional settings for video/audio.
   */
  get controls() {
    let controls = { enabled: true, native: false };
    if (this._settings.controls) {
      Object.assign(controls, this._settings.controls);
    }
    return this._settings.medium === "audio" ? true : controls;
  }
  set controls(value = { enabled: true, native: false }) {
    debug.log("[AmpConfig] SET controls:", value);
  }

  /** Getter and Setter for hls (HTTP Live Streaming) configuration. */
  get hls() {
    return {
      data: { enableWorker: true },
      quality: { startLevel: 0 },
      resources: [],
    };
  }
  set hls(value = null) {
    debug.log("[AmpConfig] SET hls:", value);
  }

  /** Getter and Setter for locales (localization) configuration. */
  get locales() {
    return super.locales;
  }
  set locales(value = {}) {
    debug.log("[AmpConfig] SET locales:", value);
    super.locales = value;
  }

  /**
   * Get media configuration for AMP player.
   * Configures video source, track, and poster.
   * @returns {Object} - Media configuration object.
   */
  get media() {
    const { _settings } = this;
    const {
      source,
      title,
      poster,
      muted,
      metadata,
      autoplay,
      videoId,
      ccInfo = [],
      track: configTrack = [],
      ccFileRoot,
      duration,
      customSource,
      startTime,
      chapters,
    } = _settings;
    const imageServer = getImageServer().includes("localhost")
      ? "img.medscapestatic.com"
      : getImageServer();

    // Configure caption tracks if available
    const xmltrack = ccInfo.map((info) => {
      const srcPrefix =
        ccFileRoot.startsWith("http") || ccFileRoot.startsWith("//")
          ? ccFileRoot
          : `https://${imageServer}${ccFileRoot}`;
      return {
        kind: "captions",
        type: "application/ttml+xml",
        srclang: info.lang,
        src: `${srcPrefix}${info.file}`,
      };
    });
    const track = [...xmltrack, ...configTrack];
    debug.log("[AmpConfig] media.track:", track);
    // Configure poster image
    const posterUrl = this.configurePoster(poster, imageServer);

    // Configure sources based on custom or default settings
    const sources = this.configureSources(source, customSource);

    // Build the media object with relevant properties
    let media = new Media();
    const mediaKeys = Object.keys(media);
    const settings = Object.entries(_settings).reduce((acc, curr, index) => {
      const [key, value] = curr;
      if (mediaKeys.includes(key)) {
        acc[key] = value;
      }
      return acc;
    }, {});
    const mediaConfig = Object.assign(media, settings, {
      title,
      poster: posterUrl,
      startTime: startTime || 0,
      autoplay,
      metadata: {
        ...metadata,
        videoId,
        chapters,
        duration: parseInt(duration),
        startTime,
        muted,
      },
      source: sources,
      track,
      duration,
    });
    debug.log("[AmpConfig] media:", mediaConfig);
    return mediaConfig;
  }

  /**
   * Configure poster URL based on given poster setting.
   * @param {string} poster - Poster URL or relative path.
   * @param {string} imageServer - Domain for image server.
   * @returns {string} - Full URL of the poster.
   */
  configurePoster(poster, imageServer) {
    if (!poster || !poster.trim()) {
      return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPjPBAACCgED8xRmJAAAAABJRU5ErkJggg==";
    }
    return poster.startsWith("http") || poster.startsWith("//")
      ? poster
      : `https://${imageServer}/${poster.startsWith("/") ? poster : `/${poster}`
      }`;
  }

  /**
   * Configure media sources based on source setting.
   * @param {string} source - Source URL or identifier.
   * @param {Array|undefined} customSource - Custom sources if provided.
   * @returns {Array} - Configured sources array.
   */
  configureSources(source, customSource) {
    if (customSource) return customSource;

    if (source.startsWith("//") || source.startsWith("http")) {
      const extension = source.split(".").pop();
      const type = MediaTypes[extension];
      return type ? [{ src: source, type }] : [];
    }

    const sourceParts = source ? source.split(",") : [];
    return medias
      .filter((media) => !media.type.startsWith("audio"))
      .map((config) => {
        const { host, endpoint, type, file } = config;
        return {
          src: `//${host}${endpoint}${sourceParts[0] || source}${file}`,
          type,
        };
      });
  }

  /** Getter and Setter for loop property. */
  get loop() {
    return this._settings.loop ?? false;
  }
  set loop(value = false) {
    debug.log("[AmpConfig] SET Loop:", value);
  }

  /** Getter for mode (default is 'html'). */
  get mode() {
    return "html";
  }
  set mode(value = "html") {
    debug.log("[AmpConfig] SET mode:", value);
  }

  /** Getter and Setter for muted property. */
  get muted() {
    debug.log("[AmpConfig] GET muted:", this._muted);
    return this._muted;
  }
  set muted(flag = this._settings.muted) {
    debug.log("[AmpConfig] SET muted:", flag);
    this._muted = flag;
  }

  /** Getter for paths configuration (plugin paths). */
  get paths() {
    return {};
  }
  set paths(paths = {}) {
    debug.log("[AmpConfig] SET paths:", paths);
  }

  /** Getter for playsinline property. */
  get playsinline() {
    return true;
  }
  set playsinline(flag = true) {
    debug.log("[AmpConfig] SET playsinline:", flag);
  }

  /** Getter and Setter for plugins configuration. */
  get plugins() {
    let { plugins: CUSTOM_PLUGINS = {}, controls = {} } = this._settings;
    const reactSettings = CUSTOM_PLUGINS.react || {};
    const isNative =
      controls.enabled === false ? true : controls.native || false;

    return {
      AmpEventsSources: {},
      TapForSound: { show: true },
      react: {
        autoHide: 3,
        native: isNative,
        resources: [],
        hint: { enable: false, mode: "" },
        share: {
          enabled: false,
        },
        ...reactSettings,
      },
      ...CUSTOM_PLUGINS,
    };
  }
  set plugins(plugins = {}) {
    debug.log("[AmpConfig] SET plugins:", plugins);
  }

  /** Getter and Setter for resources property. */
  get resources() {
    return this._settings.resources || [];
  }
  set resources(resources = []) {
    debug.log("[AmpConfig] SET resources:", resources);
  }

  /** Getter and Setter for settings property. */
  get settings() {
    return this._settings;
  }
  set settings(value = {}) {
    debug.log("[AmpConfig] SET settings:", value);
  }
}
